import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Info, Calendar } from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import Select from 'react-select';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const NominationForm = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [contractors, setContractors] = useState([]);
  const [users, setUsers] = useState([]);
  const [allWorkScopes, setAllWorkScopes] = useState([]);
  const [workScopes, setWorkScopes] = useState([]);
  const [expandedScopes, setExpandedScopes] = useState({});
  const [infoVisible, setInfoVisible] = useState(null);

  const [formData, setFormData] = useState({
    selectedContractor: '',
    selectedEmployee: '',
    startDate: '',
    endDate: '',
    status: 'pending',
  });

  const infoButtonRefs = useRef({});

  useEffect(() => {
    const token = localStorage.getItem('access_token');

    const fetchContractors = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/company/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setContractors(response.data.filter(contractor => contractor.is_ecc));
      } catch (error) {
        console.error('Error fetching contractors:', error);
      }
    };

    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/users/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    const fetchWorkScopes = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/workscopes/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const scopesWithChecked = response.data.map(scope => ({
          ...scope,
          checked: false,
          categories: scope.categories.map(cat => ({ ...cat, checked: false })),
        }));
        setAllWorkScopes(scopesWithChecked);
      } catch (error) {
        console.error('Error fetching work scopes:', error);
      }
    };

    fetchContractors();
    fetchUsers();
    fetchWorkScopes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (infoVisible && !Object.values(infoButtonRefs.current).some(ref => ref && ref.contains(event.target))) {
        setInfoVisible(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [infoVisible]);

  const handleContractorSelect = (selectedOption) => {
  const selectedId = selectedOption ? selectedOption.value : '';
  setFormData(prev => ({ ...prev, selectedContractor: selectedId }));
  const filteredScopes = allWorkScopes.filter(scope => scope.company && scope.company === parseInt(selectedId, 10));
  setWorkScopes(filteredScopes);
  // Ustaw expandedScopes dla scope'ów, które mają kategorie
  const newExpandedScopes = filteredScopes.reduce((acc, scope) => {
    if (scope.categories && scope.categories.length > 0) {
      acc[scope.id] = true;
    }
    return acc;
  }, {});
  setExpandedScopes(newExpandedScopes);
};

  const handleEmployeeSelect = (selectedOption) => {
    setFormData(prev => ({ ...prev, selectedEmployee: selectedOption ? selectedOption.value : '' }));
  };

  const handleWorkScopeChange = (scopeId) => {
    setWorkScopes(prev =>
      prev.map(scope => {
        if (scope.id === scopeId) {
          const newChecked = !scope.checked;
          return {
            ...scope,
            checked: newChecked,
            categories: scope.categories.map(cat => ({
              ...cat,
              checked: newChecked,
            })),
          };
        }
        return scope;
      })
    );
  };

  const handleCategoryChange = (scopeId, categoryId) => {
    setWorkScopes(prev =>
      prev.map(scope => {
        if (scope.id === scopeId) {
          const updatedCategories = scope.categories.map(cat =>
            cat.id === categoryId ? { ...cat, checked: !cat.checked } : cat
          );
          const anyCategoryChecked = updatedCategories.some(cat => cat.checked);
          return {
            ...scope,
            checked: anyCategoryChecked,
            categories: updatedCategories,
          };
        }
        return scope;
      })
    );
  };

  const toggleExpandScope = (scopeId) => {
    setExpandedScopes(prev => ({
      ...prev,
      [scopeId]: !prev[scopeId],
    }));
  };

  const toggleInfo = (id) => {
    setInfoVisible(prev => (prev === id ? null : id));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === "startDate" && prev.endDate && value > prev.endDate ? { endDate: "" } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedScopes = workScopes
      .filter(scope => scope.checked)
      .map(scope => ({
        id: scope.id,
        categories: scope.categories.filter(cat => cat.checked).map(cat => cat.id),
      }));

    const submitData = {
      work_scopes: selectedScopes.map(s => s.id),
      categories: selectedScopes.flatMap(s => s.categories),
      user: formData.selectedEmployee,
      company_to: formData.selectedContractor,
      company: user.company.id,
      date_start: formData.startDate,
      date_end: formData.endDate,
      status: 'pending',
    };

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API_BASE_URL}/nomination/`, submitData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Nomination submitted:', response.data);      
      navigate('/nominations/list', { replace: true });
    } catch (error) {
      console.error('Error during submission:', error.response?.data || error.message);
      alert('Error while sending nomination. Please try again.');
    }
  };

  const isSubmitDisabled = !(
    formData.selectedContractor &&
    formData.selectedEmployee &&
    formData.startDate &&
    formData.endDate &&
    workScopes.some(scope => scope.checked)
  );

  const userQualifications = (userId) => {
    const selectedUser = users.find(u => u.id.toString() === userId);
    return selectedUser?.qualifications.map(q => q.qualification_type) || [];
  };

  const meetsWorkScopeRequirements = (scope, quals) => {
    const scopeDocs = (scope.required_documents_data || []).map(doc => doc.id);
    return scopeDocs.every(docId => quals.includes(docId));
  };

  const meetsCategoryRequirements = (scope, category, quals) => {
    const scopeDocs = (scope.required_documents_data || []).map(doc => doc.id);
    const catDocs = (category.required_documents_data || []).map(doc => doc.id);
    const catGroups = category.required_doc_groups_category || [];
    
    const meetsScopeDocs = scopeDocs.every(docId => quals.includes(docId));
    const meetsCatDocs = catDocs.every(docId => quals.includes(docId));
    const meetsGroups = catGroups.every(group => group.some(docId => quals.includes(docId)));
    
    return meetsScopeDocs && meetsCatDocs && meetsGroups;
  };

  const getWorkScopeColor = (scope) => {
    if (!formData.selectedEmployee) return 'text-slate-600';
    const quals = userQualifications(formData.selectedEmployee);
    const meetsScope = meetsWorkScopeRequirements(scope, quals);

    if (scope.categories.length === 0) {
      return meetsScope ? 'text-green-600' : 'text-red-600';
    }

    const categoryResults = scope.categories.map(cat => meetsCategoryRequirements(scope, cat, quals));
    const allMet = categoryResults.every(result => result);
    const noneMet = categoryResults.every(result => !result);
    return allMet ? 'text-green-600' : noneMet ? 'text-red-600' : 'text-slate-600';
  };

  const getCategoryColor = (scope, category) => {
    if (!formData.selectedEmployee) return 'text-slate-600';
    const quals = userQualifications(formData.selectedEmployee);
    return meetsCategoryRequirements(scope, category, quals) ? 'text-green-600' : 'text-red-600';
  };

  const getModelsText = (modelsData) => {
    if (!modelsData || modelsData.length === 0) {
      return (
        <div className="text-xs">
          <span className="whitespace-nowrap">Valid for turbine models:</span>
          <div className="mt-1">None</div>
        </div>
      );
    }
  
    return (
      <div className="text-xs">
        <span className="whitespace-nowrap">Valid for models:</span>
        {modelsData.map((model, index) => (
          <div key={index} className="mt-1">{model.name}</div>
        ))}
      </div>
    );
  };

  const selectedWorkScopes = workScopes.filter(scope => scope.checked);
  const selectedCategories = selectedWorkScopes.flatMap(scope => scope.categories.filter(cat => cat.checked));

  const requiredDocuments = Array.from(
    new Map(
      [
        ...selectedWorkScopes.flatMap(scope => scope.required_documents_data || []),
        ...selectedCategories.flatMap(cat => cat.required_documents_data || []),
      ].map(doc => [doc.id, doc])
    ).values()
  );

  const requiredGroups = Array.from(
    new Map(
      selectedCategories
        .flatMap(cat => cat.required_doc_groups_category || [])
        .map((group, index) => [`group-${index}`, group])
    ).values()
  );

  const meetsRequirements = (userId) => {
    const quals = userQualifications(userId);
    const meetsAllDocs = requiredDocuments.every(doc => quals.includes(doc.id));
    const meetsGroups = requiredGroups.every(group => group.some(docId => quals.includes(docId)));
    return meetsAllDocs && meetsGroups;
  };

  // Opcje dla react-select
  const contractorOptions = contractors.map(contractor => ({
    value: contractor.id.toString(),
    label: contractor.name,
  }));

  const employeeOptions = users.map(user => ({
    value: user.id.toString(),
    label: `${user.first_name} ${user.last_name}`,
    meetsRequirements: meetsRequirements(user.id.toString()),
  }));

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      width: '100%',
      minHeight: '38px',
      border: '1px solid #D9D9D9',
      borderRadius: '0.75rem',
      padding: '0 0.5rem',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      backgroundColor: '#fff',
      color: '#334155',
      boxShadow: state.isFocused ? '0 0 0 1px #FE3F06' : 'none',
      outline: 'none',
      '&:hover': {
        borderColor: '#FE3F06',
      },
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
      color: '#334155',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#64748b',
      fontSize: '0.875rem',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#334155',
      fontSize: '0.875rem',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.75rem',
      border: '1px solid #D9D9D9',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginTop: '0.25rem',
      zIndex: 20,
      backgroundColor: '#fff',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#FEF2F0' : '#fff',
      color: state.data.meetsRequirements !== undefined
        ? (state.data.meetsRequirements ? '#16a34a' : '#dc2626')
        : '#334155',
      fontSize: '0.875rem',
      padding: '0.5rem 0.75rem',
      '&:hover': {
        backgroundColor: '#FEF2F0',
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#64748b',
      padding: '0.25rem',
      '&:hover': {
        color: '#FE3F06',
      },
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#64748b',
      padding: '0.25rem',
      '&:hover': {
        color: '#FE3F06',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      padding: '0',
      display: 'flex',
      alignItems: 'center',
    }),
  };

  const inputClasses = 'w-full px-2 py-1.5 text-sm border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500';
  const labelClasses = 'block text-xs font-medium text-slate-700 mb-1';

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto max-h-screen overflow-hidden">
        <div className="min-h-[calc(100vh-60px)] max-w-3xl mx-auto bg-white rounded-xl shadow-lg border border-steel-200 my-6 overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-brand-50 to-steel-50 border-b border-steel-200">
            <h2 className="text-lg font-semibold text-slate-900">Create New Nomination</h2>
          </div>

          <div className="p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelClasses}>Request Recipient</label>
                <Select
                  options={contractorOptions}
                  value={contractorOptions.find(option => option.value === formData.selectedContractor) || null}
                  onChange={handleContractorSelect}
                  placeholder="Select a recipient"
                  isClearable
                  styles={customStyles}
                  required
                />
              </div>

              {formData.selectedContractor && (
                <div className="p-4 bg-steel-50 rounded-xl">
                  <h3 className="text-sm font-medium text-slate-700">Select authorization levels and platforms</h3>
                  <div className="p-2 space-y-2">
                    {workScopes.map(scope => (
                      <div key={scope.id}>
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={scope.checked}
                            onChange={() => handleWorkScopeChange(scope.id)}
                            className="h-4 w-4 text-brand-500 rounded border-steel-300 focus:ring-brand-500"
                          />
                          <div className="flex items-center space-x-2 flex-grow">
                            <span
                              onClick={() => toggleExpandScope(scope.id)}
                              className={`text-sm cursor-pointer hover:text-brand-600 ${getWorkScopeColor(scope)}`}
                            >
                              {scope.name}
                            </span>
                            <div className="relative inline-block">
                              <button
                                type="button"
                                ref={el => (infoButtonRefs.current[`scope-${scope.id}`] = el)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleInfo(`scope-${scope.id}`);
                                }}
                                className="text-slate-500 hover:text-brand-500"
                              >
                                <Info className="h-4 w-4" />
                              </button>
                              {infoVisible === `scope-${scope.id}` && (
                                <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 p-2 bg-slate-800 text-white text-xs rounded-xl shadow-lg z-10 max-w-sm">
                                  {getModelsText(scope.models_data)}
                                </div>
                              )}
                            </div>
                          </div>
                          {scope.categories.length > 0 && (
                            <button
                              type="button"
                              onClick={() => toggleExpandScope(scope.id)}
                              className="text-slate-500 hover:text-brand-500"
                            >
                              {expandedScopes[scope.id] ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                        {expandedScopes[scope.id] && scope.categories.length > 0 && (
                          <div className="ml-6 mt-2 space-y-2">
                            {scope.categories.map(category => (
                              <div key={category.id} className="flex items-start space-x-3">
                                <label className="flex items-start space-x-3 cursor-pointer flex-grow">
                                  <input
                                    type="checkbox"
                                    checked={category.checked}
                                    onChange={() => handleCategoryChange(scope.id, category.id)}
                                    className="h-4 w-4 text-brand-500 rounded border-steel-300 focus:ring-brand-500"
                                  />
                                  <div className="flex items-center space-x-2">
                                    <span className={`text-sm ${getCategoryColor(scope, category)}`}>
                                      {category.name}
                                    </span>
                                    <div className="relative inline-block">
                                      <button
                                        type="button"
                                        ref={el => (infoButtonRefs.current[`cat-${category.id}`] = el)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleInfo(`cat-${category.id}`);
                                        }}
                                        className="text-slate-500 hover:text-brand-500"
                                      >
                                        <Info className="h-4 w-4" />
                                      </button>
                                      {infoVisible === `cat-${category.id}` && (
                                        <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 p-2 bg-slate-800 text-white text-xs rounded-xl shadow-lg z-10 max-w-sm">
                                          {getModelsText(category.models_data || scope.models_data)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className={labelClasses}>Select Employee</label>
                <Select
                  options={employeeOptions}
                  value={employeeOptions.find(option => option.value === formData.selectedEmployee) || null}
                  onChange={handleEmployeeSelect}
                  placeholder="Select an employee"
                  isClearable
                  styles={customStyles}
                  required
                />
              </div>

              {formData.selectedEmployee && (
                <div className="p-4 bg-steel-50 rounded-xl">
                  <h3 className="text-sm font-medium text-slate-700">Employee Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Name:</span>
                      <span className="ml-2 text-slate-900">
                        {users.find(user => user.id.toString() === formData.selectedEmployee)?.first_name}{' '}
                        {users.find(user => user.id.toString() === formData.selectedEmployee)?.last_name}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-slate-700">Required Documents for Selected Scopes and Platforms</h4>
                    <div className="mt-2 space-y-4">
                      {requiredDocuments.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-slate-600">All Required Documents</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                            {requiredDocuments.map((doc, index) => {
                              const selectedUser = users.find(u => u.id.toString() === formData.selectedEmployee);
                              const userHasDocument = selectedUser?.qualifications.some(q => q.qualification_type === doc.id);
                              return (
                                <div key={index} className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={userHasDocument}
                                    disabled
                                    className={`h-4 w-4 rounded border-steel-300 ${userHasDocument ? 'text-green-600' : 'text-red-600'}`}
                                  />
                                  <label
                                    className={`text-sm ${userHasDocument ? 'text-green-700' : 'text-red-700'}`}
                                  >
                                    {doc.name}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {requiredGroups.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-slate-600">Alternative Group Requirements (One of each)</h5>
                          {requiredGroups.map((group, groupIndex) => {
                            const selectedUser = users.find(u => u.id.toString() === formData.selectedEmployee);
                            const userQuals = selectedUser?.qualifications.map(q => q.qualification_type) || [];
                            const groupData = selectedCategories
                              .flatMap(cat => cat.required_doc_groups_category_data || [])
                              .find((g, idx) => JSON.stringify(g.map(d => d.id)) === JSON.stringify(group));
                            const groupMet = group.some(docId => userQuals.includes(docId));
                            return (
                              <div
                                key={groupIndex}
                                className={`mt-2 p-3 border border-steel-300 rounded-xl ${groupMet ? 'bg-green-100' : 'bg-red-100'}`}
                              >
                                <p className="text-sm font-medium text-slate-700">Group {groupIndex + 1}</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
                                  {(groupData || []).map((doc, docIndex) => {
                                    const userHasDocument = userQuals.includes(doc.id);
                                    return (
                                      <div key={docIndex} className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          checked={userHasDocument}
                                          disabled
                                          className={`h-4 w-4 rounded border-steel-300 ${userHasDocument ? 'text-green-600' : 'text-slate-400'}`}
                                        />
                                        <label
                                          className={`text-sm ${userHasDocument ? 'text-green-700' : 'text-slate-700'}`}
                                        >
                                          {doc.name}
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                                <p className={`text-xs mt-1 ${groupMet ? 'text-green-600' : 'text-red-600'}`}>
                                  {groupMet ? 'Requirement met' : 'At least one document required'}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {requiredDocuments.length === 0 && requiredGroups.length === 0 && (
                        <p className="text-sm text-slate-500">No requirements defined for selected scopes and platforms.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-end space-x-2">
                  <div className="flex-grow">
                    <label className={labelClasses}>Valid From</label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className={inputClasses}
                      required
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date().toISOString().split("T")[0];
                      setFormData(prev => ({ ...prev, startDate: today }));
                    }}
                    className="px-3 py-1 text-sm bg-brand-100 hover:bg-steel-200 rounded-xl"
                  >
                    Today
                  </button>
                </div>

                <div className="flex items-end space-x-2">
                  <div className="flex-grow">
                    <label className={labelClasses}>Valid Until</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      className={inputClasses}
                      required
                      min={formData.startDate || new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const startDate = formData.startDate ? new Date(formData.startDate) : today;
                      const nextYear = new Date(startDate);
                      nextYear.setFullYear(nextYear.getFullYear() + 3);
                      setFormData(prev => ({
                        ...prev,
                        startDate: prev.startDate || today.toISOString().split("T")[0],
                        endDate: nextYear.toISOString().split("T")[0]
                      }));
                    }}
                    className="px-3 py-1 text-sm bg-brand-100 hover:bg-steel-200 rounded-xl"
                  >
                    Three Years
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitDisabled}
                className={`w-full px-3 py-2 text-sm rounded-xl transition-colors ${isSubmitDisabled ? 'bg-steel-300 text-slate-500 cursor-not-allowed' : 'bg-brand-500 text-white hover:bg-brand-600 shadow-lg shadow-brand-500/20'}`}
              >
                Send Nomination for Signature
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NominationForm;